/**
 * SetupMenu.jsx — "Story Setup" top-level menu.
 *
 * Groups every "start a new story" flow: framework builder, starter
 * templates, synopsis tools, manuscript import, model roles, and
 * planning-PDF download.
 */
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuShortcut,
} from '@/components/ui/dropdown-menu';
import { Sparkles, FileText, FileUp, Library, ScrollText, SlidersHorizontal, Download, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';
import { Menu, MenuItem, GatedMenuItem, ParchmentTabIcon } from './shared';

export function SetupMenu({
  onNewFromTemplate, onImportManuscript, visible, togglePanel,
  onOpenSynopsisHub, onOpenModelRoles, onOpenAiAudit,
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  return (
    <Menu label={t('menu.setup')} icon={ParchmentTabIcon}>
      <DropdownMenuLabel className="text-[11px] uppercase tracking-wider text-muted-foreground">
        {t('menu.startAStoryLabel')}
      </DropdownMenuLabel>
      <MenuItem
        onClick={() => { window.location.href = '/framework'; }}
        data-testid="menu-setup-framework"
        tip="Map your story's structure — genre, tone, characters — before you start writing"
      >
        <Sparkles className="h-3.5 w-3.5" /> {t('menu.buildNovelFramework')}
      </MenuItem>
      <GatedMenuItem capability="new_from_template" onClick={onNewFromTemplate} data-testid="menu-setup-new-from-template"
        tip="Start with a pre-built scaffold matched to your chosen genre">
        <FileText className="h-3.5 w-3.5" /> {t('menu.newFromTemplateFull')}
      </GatedMenuItem>
      <DropdownMenuSeparator />
      <DropdownMenuLabel className="text-[11px] uppercase tracking-wider text-muted-foreground">
        {t('menu.importDraftLabel')}
      </DropdownMenuLabel>
      <MenuItem onClick={onImportManuscript} data-testid="menu-setup-import-manuscript"
        tip="Load a .docx or .txt file as your working draft">
        <FileUp className="h-3.5 w-3.5" /> {t('menu.importManuscript')}
        <DropdownMenuShortcut>Ctrl+Shift+I</DropdownMenuShortcut>
      </MenuItem>
      <MenuItem onClick={() => navigate('/manuscript-library')} data-testid="menu-setup-manuscript-library"
        tip="Browse all the novel drafts you have stored in the system">
        <Library className="h-3.5 w-3.5" /> {t('menu.manuscriptLibrary')}
      </MenuItem>
      <GatedMenuItem capability="import_synopsis" onClick={onOpenSynopsisHub} data-testid="menu-setup-synopsis-tools"
        tip="Import or AI-generate a full story plan from your logline or summary">
        <ScrollText className="h-3.5 w-3.5" /> {t('menu.synopsisTools')}
        <DropdownMenuShortcut>Ctrl+Alt+N</DropdownMenuShortcut>
      </GatedMenuItem>
      <MenuItem
        data-testid="menu-setup-read-manuscript"
        tip="Let the AI scan your prose and auto-populate your Character Bible"
        onClick={() => {
          if (!visible.characters) togglePanel('characters');
          setTimeout(() => window.dispatchEvent(new Event('story-bible-autofill')), 700);
        }}
      >
        <Sparkles className="h-3.5 w-3.5" /> {t('menu.readManuscriptFillBible')}
      </MenuItem>
      <MenuItem data-testid="menu-setup-model-roles" onClick={onOpenModelRoles}
        tip="Choose which AI models handle your drafting, analysis, and feedback">
        <SlidersHorizontal className="h-3.5 w-3.5" /> {t('menu.modelRoles')}
      </MenuItem>
      <DropdownMenuSeparator />
      <MenuItem
        data-testid="menu-setup-download-framework"
        tip="Save the Crown & Rose Novel Framework as a printable PDF guide"
        onClick={() => {
          const API_BASE = (process.env.REACT_APP_BACKEND_URL || '') + '/api';
          window.open(`${API_BASE}/exports/novel-framework.pdf`, '_blank');
          toast.success(t('menu.downloadingFramework'));
        }}
      >
        <Download className="h-3.5 w-3.5" /> {t('menu.downloadFrameworkPdf')}
      </MenuItem>
      <DropdownMenuSeparator />
      <MenuItem
        data-testid="menu-setup-ai-content-audit"
        tip="Check whether your manuscript meets AI-generated content disclosure requirements"
        onClick={onOpenAiAudit}
      >
        <ShieldCheck className="h-3.5 w-3.5 text-amber-400" /> AI Content Audit
      </MenuItem>
    </Menu>
  );
}
